import React from 'react';
import {
  StyledContainerCity,
  StyledContainerState,
  StyledContainerZIPCountry,
  StyledCountryContainer,
  StyledCountryDropdown,
  StyledEdit,
  StyledInputWithValidation,
  StyledLabel,
  StyledLabelContainerWithEdit,
} from './ApplicationEmployment.style';
import styled from 'styled-components/macro';
import { Input, StrictDropdownProps } from 'semantic-ui-react';

export enum AddressType {
  BILLING = 'billingAddress',
  SHIPPING = 'shippingAddress',
}

export interface Address {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface AddressFormProps {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  disabled: boolean;
  addressType: AddressType;
  countryOptions?: { key: number; value: string; text: string; image?: string }[];
  addressLine2ErrorMessage?: string;
  showEditButton?: boolean;
  addressFormErrors?: {
    line1?: boolean;
    line2?: boolean;
    city?: boolean;
    state?: boolean;
    zipCode?: boolean;
  };
  handleAddressLineChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    field: keyof Address,
    addressType: AddressType
  ) => void;
  handleAddressCountryChange?: (
    event: React.SyntheticEvent<Element, Event>,
    data: StrictDropdownProps,
    addressType: AddressType
  ) => void;
  handleEditChange?: () => void;
}

const AddressForm: React.FC<AddressFormProps> = ({
  addressLine1,
  addressLine2,
  city,
  state,
  postalCode,
  country,
  disabled,
  addressType,
  countryOptions = [],
  showEditButton = false,
  addressLine2ErrorMessage = '',
  addressFormErrors = {
    line1: false,
    line2: false,
    city: false,
    state: false,
    zipCode: false,
  },
  handleAddressLineChange = () => {},
  handleAddressCountryChange = () => {},
  handleEditChange = () => {},
}) => {
  return (
    <>
      <StyledLabelContainerWithEdit>
        <StyledLabel>
          {addressType === AddressType.SHIPPING ? 'Shipping address line 1' : 'Business address line 1'}
        </StyledLabel>
        {showEditButton && <StyledEdit onClick={handleEditChange}>Edit</StyledEdit>}
      </StyledLabelContainerWithEdit>
      <StyledInput
        placeholder="Address line 1"
        testId="address-line-1"
        value={addressLine1}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          handleAddressLineChange(event, 'addressLine1', addressType)
        }
        disabled={disabled}
        hasError={addressFormErrors?.line1}
      />
      <StyledLabel>
        {addressType === AddressType.SHIPPING
          ? 'Shipping address line 2 (if applicable)'
          : 'Business address line 2 (if applicable)'}
      </StyledLabel>
      <StyledInputWithValidation
        type="text"
        name="address-line-2"
        placeholder="Address line 2"
        testId="address-line-2"
        value={addressLine2}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          handleAddressLineChange(event, 'addressLine2', addressType)
        }
        disabled={disabled}
        isCorrect={!addressFormErrors?.line2}
        errorMessage={addressLine2ErrorMessage}
      />
      <StyledContainerCity>
        <StyledLabel>City</StyledLabel>
        <StyledInput
          placeholder="City"
          testId="address-city"
          value={city}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleAddressLineChange(event, 'city', addressType)}
          disabled={disabled}
          hasError={addressFormErrors?.city}
        />
      </StyledContainerCity>
      <StyledContainerZIPCountry>
        <StyledContainerState>
          <StyledLabel>State</StyledLabel>
          <StyledInput
            placeholder="State"
            testId="state"
            value={state}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleAddressLineChange(event, 'state', addressType)
            }
            disabled={disabled}
            hasError={addressFormErrors?.state}
          />
        </StyledContainerState>
        <div>
          <StyledLabel>ZIP/Postal Code</StyledLabel>
          <StyledInput
            placeholder="ZIP/Postal Code"
            testId="code"
            value={postalCode}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleAddressLineChange(event, 'zipCode', addressType)
            }
            disabled={disabled}
            hasError={addressFormErrors?.zipCode}
          />
        </div>
      </StyledContainerZIPCountry>
      <StyledLabel>Country</StyledLabel>
      <StyledCountryContainer>
        <StyledCountryDropdown
          scrolling
          selection
          selected={country}
          testId="country"
          options={countryOptions}
          onChange={(event: React.SyntheticEvent<Element, Event>, data: StrictDropdownProps) =>
            handleAddressCountryChange(event, data, addressType)
          }
          disabled={disabled}
        />
      </StyledCountryContainer>
    </>
  );
};

const StyledInput = styled(({ hasError, ...rest }) => <Input {...rest} />)`
  &&& {
    display: flex;
    margin-bottom: ${props => props.theme.pxToRem(7)};
  }

  &&&.disabled {
    cursor: not-allowed;
    opacity: 1;
    input {
      border-color: ${props => props.theme.colors.neutralGrey4};
      background-color: ${props => props.theme.colors.neutralGrey2};
    }
  }

  ${({ hasError, theme }) =>
    hasError &&
    `
      &&&& > input {
        :focus {
          border: solid ${theme.pxToRem(1)} ${theme.colors.interfaceRed};
        }
         border: solid ${theme.pxToRem(1)} ${theme.colors.interfaceRed};
      }
    `}
`;

export default AddressForm;
